import React, { useEffect, useState } from "react";
import Layout, { Container } from "../components/layout";
import {
  FaBoxes,
  FaTruck,
  FaExclamationTriangle,
  FaWarehouse,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { apiurl } from "../config/config";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  let materialTypeCounts = [];
  if (data && data.materialTypeCounts) {
    materialTypeCounts = data.materialTypeCounts.map((item) => ({
      materialType: item.materialType.replace("_", " "),
      count: item.count,
    }));
  }

  // ✅ Fetch from API
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await fetch(`${apiurl}/api/dashboard/fms-dashboard`);
        const json = await res.json();
        if (json.success) {
          setData(json.data);
        }
      } catch (err) {
        console.error("Error fetching dashboard:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <Layout>
        <Container>
          <div className="p-6 text-center">Loading dashboard...</div>
        </Container>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <Container>
          <div className="p-6 text-center text-red-500">
            Failed to load dashboard
          </div>
        </Container>
      </Layout>
    );
  }

  // ✅ Map API data into stats
  const stats = [
    {
      title: "Total Materials",
      value: data.allMaterials,
      sub: "Registered SKUs",
      icon: <FaBoxes className="w-5 h-5" />,
      color: "bg-indigo-500",
      link:"/materials",
    },
    {
      title: "Total Suppliers",
      value: data.allSuppliers,
      sub: "Active vendors",
      icon: <FaTruck className="w-5 h-5" />,
      color: "bg-green-500",
      link:"/suppliers",
    },
    {
      title: "Low Stock Items",
      value: data.lowestQtyMaterials.length,
      sub: `Below ${data.lowStockLimit}`,
      icon: <FaExclamationTriangle className="w-5 h-5" />,
      color: "bg-yellow-500",
      link:"/inventory",
    },
    {
      title: "Material Types",
      value: data.materialTypeCounts.length,
      sub: "Categories",
      icon: <FaWarehouse className="w-5 h-5" />,
      color: "bg-rose-500",
    },
  ];

  return (
    <Layout>
      <Container overflow="overflow-auto">
        <div className="p-2 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold">
                Factory Dashboard
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Inventory & material overview — live snapshot
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 px-4 py-2 rounded border border-gray-200 bg-white hover:shadow-sm"
              >
                Refresh
              </button>
              <Link
                to="/purchase-orders/create"
                className="flex items-center gap-2 px-4 py-2 bg-[#4B49AC] text-white rounded"
              >
                New Purchase Order
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s, idx) => (
              <Link
                to={s.link || "#"}
                key={idx}
                className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition border border-gray-200"
              >
                <div className={`p-3 rounded-lg text-white ${s.color}`}>
                  {s.icon}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">{s.title}</p>
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-xl font-bold">{s.value}</h2>
                    {s.sub && (
                      <span className="text-xs text-gray-400">{s.sub}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {materialTypeCounts.map((item, idx) => (
              <div
                key={idx}
                className="px-5 py-3 bg-white rounded-2xl border border-gray-200"
              >
                <h2 className="text-sm font-semibold capitalize">
                  {item.materialType.replace("_", " ")}
                </h2>
                <p className="text-2xl font-bold text-indigo-600">
                  {item.count}
                </p>
              </div>
            ))}
          </div>

          {/* Low Stock Table */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Low Stock Items</h3>
              <div className="text-sm text-gray-500">
                Showing {data.lowestQtyMaterials.length}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs text-gray-500">
                  <tr>
                    <th className="py-2 pr-4">ID</th>
                    <th className="py-2 pr-4">Material</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Quantity</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {data.lowestQtyMaterials.map((row) => (
                    <tr key={row.id} className="border-t">
                      <td className="py-3 pr-4 font-medium">{row.id}</td>
                      <td className="py-3 pr-4">{row.name}</td>
                      <td className="py-3 pr-4">{row.type}</td>
                      <td className="py-3 pr-4">{row.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Container>
    </Layout>
  );
}